'use client'

import { search, type SearchResult } from '@/actions/search'
import { useDebounce } from '@payloadcms/ui'
import { AnimatePresence, motion } from 'motion/react'
import Form from 'next/form'
import Link from 'next/link'
import { useEffect, useState, useTransition, type FC } from 'react'
import { EASE_OUT_CUBIC } from '../../helpers/timing'
import { Corridor } from '../atoms/Corridor'
import { SearchBar } from './SearchBar'

type SearchState =
	| { value: 'idle'; term: string }
	| { value: 'results'; term: string; results: SearchResult[] }

export const Search: FC = () => {
	const [searchTerm, setSearchTerm] = useState('')
	const debouncedSearchTerm = useDebounce(searchTerm, 300).trim()

	const [state, setState] = useState<SearchState>({ value: 'idle', term: '' })
	const [isPending, startTransition] = useTransition()

	useEffect(() => {
		if (debouncedSearchTerm !== '') {
			setState({ value: 'idle', term: debouncedSearchTerm })
			startTransition(async () => {
				const { results } = await search(debouncedSearchTerm, { limit: 5 })
				setState((prev) => {
					if (debouncedSearchTerm !== prev.term) return prev
					return { ...prev, value: 'results', results }
				})
			})
		}
	}, [debouncedSearchTerm])

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value)
	}

	return (
		<div className="relative z-10 pt-5xl tablet-l:pt-4xl pb-6xl">
			<Corridor>
				<div className="w-full border-b-(length:--stroke-xs) border-b-blue-tint-40 pb-xl mb-4xl">
					<p className="text-h4 text-blue-tint-10">What are you looking for?</p>
				</div>
				<Form action="/search">
					<SearchBar
						theme="blue"
						value={searchTerm}
						onChange={handleSearchChange}
						loading={isPending}
						autoFocus
					/>
				</Form>
				{/* Search Results */}
				<AnimatePresence mode="wait">
					{state.value === 'results' && (
						<motion.div
							key={state.term}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3, ease: EASE_OUT_CUBIC }}
							className="mt-2xl text-body-md"
						>
							{state.results.length === 0 ? (
								<motion.div
									key="no-results"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="text-blue-tint-10"
								>
									No results found for <strong>{state.term}</strong>
								</motion.div>
							) : (
								<motion.div
									key="results"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="space-y-lg"
								>
									<p className="text-blue-tint-10">
										Top results for <strong>{state.term}</strong>
									</p>
									<div className="flex flex-col items-start gap-y-md">
										{state.results.map((result) => (
											<Link
												key={result.url}
												href={result.url}
												className="block text-md underline underline-offset-2"
											>
												{result.title}
											</Link>
										))}
									</div>
								</motion.div>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</Corridor>
		</div>
	)
}
